// Aesthetic: evidence panel - the scan itself, sized to be read, with nothing
// competing for attention. Slate frame, page chrome only where it earns space.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  AlertCircle,
  Loader2,
  RotateCw,
  Crop,
  RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Same worker resolution the splitter uses: CDN, falling back to the copy in public/.
try {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
} catch {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.5.3.93.min.mjs';
}

/** Anything darker than this counts as ink rather than scanner bed. */
const INK_LUMINANCE = 238;
/** Breathing room left around detected content, as a fraction of the page. */
const TRIM_PADDING = 0.012;
/** Below this the crop is treated as a mis-detection and ignored. */
const MIN_TRIM_AREA = 0.04;
/** Assumed page shape until the real one is known - a portrait letter sheet. */
const PLACEHOLDER_ASPECT = 11 / 8.5;

/**
 * Finds the bounding box of the ink on a rendered page.
 *
 * These scans are whole A3 sheets with a letter-size invoice somewhere on them,
 * so most of the page is empty scanner bed and the invoice ends up rendered
 * small. Detecting where the content actually is lets the archived copy be the
 * document rather than the bed it was lying on.
 *
 * Returns a rect normalised 0..1 against the rendered (already rotated) page,
 * or null when the page is blank or covered edge to edge - in both cases there
 * is nothing useful to trim.
 */
function detectContentBounds(canvas) {
  const { width, height } = canvas;
  if (!width || !height) return null;

  let pixels;
  try {
    pixels = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, width, height).data;
  } catch {
    return null; // tainted canvas - nothing to be done about it here
  }

  // Sampling every few pixels is plenty to find an edge and keeps a large scan
  // from blocking the main thread.
  const step = Math.max(1, Math.round(Math.min(width, height) / 400));
  // A single speck of dust should not define the edge of the page.
  const minInkPerRow = Math.max(2, Math.floor((width / step) * 0.004));
  const minInkPerCol = Math.max(2, Math.floor((height / step) * 0.004));

  const rowInk = new Array(height).fill(0);
  const colInk = new Array(width).fill(0);

  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      if (pixels[i + 3] < 16) continue; // transparent
      // Rough luminance is more than good enough to separate ink from paper.
      const luma = (pixels[i] * 299 + pixels[i + 1] * 587 + pixels[i + 2] * 114) / 1000;
      if (luma < INK_LUMINANCE) {
        rowInk[y]++;
        colInk[x]++;
      }
    }
  }

  let top = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += step) {
    if (rowInk[y] >= minInkPerRow) {
      if (top < 0) top = y;
      bottom = y;
    }
  }

  let left = -1;
  let right = -1;
  for (let x = 0; x < width; x += step) {
    if (colInk[x] >= minInkPerCol) {
      if (left < 0) left = x;
      right = x;
    }
  }

  if (top < 0 || left < 0 || bottom <= top || right <= left) return null;

  const rect = {
    x: Math.max(0, left / width - TRIM_PADDING),
    y: Math.max(0, top / height - TRIM_PADDING),
    width: Math.min(1, (right - left) / width + TRIM_PADDING * 2),
    height: Math.min(1, (bottom - top) / height + TRIM_PADDING * 2)
  };
  rect.width = Math.min(rect.width, 1 - rect.x);
  rect.height = Math.min(rect.height, 1 - rect.y);

  if (rect.width * rect.height < MIN_TRIM_AREA) return null; // implausible
  // Already tight: cropping would gain nothing and only risks clipping.
  if (rect.width > 0.97 && rect.height > 0.97) return null;

  return rect;
}

/** Turns a crop rect a quarter turn clockwise, so it follows the content. */
export function rotateRectClockwise(rect) {
  if (!rect) return null;
  return {
    x: 1 - rect.y - rect.height,
    y: rect.x,
    width: rect.height,
    height: rect.width
  };
}

const clamp01 = (n) => Math.min(1, Math.max(0, n));

/**
 * Shows only the pages belonging to one proposed invoice.
 *
 * A scan batch can hold several invoices, so showing the whole file would make
 * the reviewer find the right pages themselves - which is the work we are trying
 * to remove. Navigation is clamped to [startPage, endPage].
 *
 * Rotation and crop are owned by the caller, because they are part of what gets
 * approved: the archived PDF is whatever the reviewer was looking at.
 */
export function ReviewPreview({
  url,
  startPage,
  endPage,
  fileName,
  onExpand,
  className,
  rotation = 0,
  crops = {},
  onTransformChange,
  // Evidence rather than something being edited: page through it, but no
  // rotate or crop. Offering controls that cannot change what gets saved is
  // worse than offering none.
  readOnly = false
}) {
  const [numPages, setNumPages] = useState(null);
  const [current, setCurrent] = useState(startPage || 1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(420);
  const [intrinsicRotation, setIntrinsicRotation] = useState(0);
  // The page's own dimensions, unrotated. The box the page sits in is sized
  // from these rather than from whatever orientation it is currently in.
  const [pageSize, setPageSize] = useState(null);
  const [drag, setDrag] = useState(null);
  // Cropping is a mode, not a permanent overlay, and it starts off: most scans
  // arrive framed correctly and an automatic trim on those only shaves the
  // margins by a different amount on every sheet. Off, the page is bare and the
  // whole sheet is filed; turning it on trims to whatever they are looking at.
  const [cropEnabled, setCropEnabled] = useState(false);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const pageBoxRef = useRef(null);
  const autoTrimmed = useRef(new Set());

  const first = startPage || 1;
  const last = endPage || startPage || numPages || 1;
  const pageCount = Math.max(1, last - first + 1);

  // What the reviewer is actually looking at: the page's own rotation plus
  // whatever they have asked for. The backend adds the same two together.
  const displayRotation = (((intrinsicRotation + rotation) % 360) + 360) % 360;

  // One footprint for all four rotations.
  //
  // Rendering the page at the container's width means a quarter turn flips the
  // aspect ratio, the element's height changes by hundreds of pixels, and the
  // card - along with everything under it - jumps. So the box is measured once
  // from the page's upright shape and never turns with it; the page is scaled
  // to fit inside whichever way round it currently is. Rotating changes what
  // the reviewer sees, not where it sits.
  const viewport = useMemo(() => {
    const quarterTurn = (deg) => deg % 180 !== 0;

    if (!pageSize?.width || !pageSize?.height) {
      // Nothing measured yet. Assuming a portrait sheet beats letting the box
      // resize under the reviewer the moment the first page arrives.
      return { boxHeight: Math.round(width * PLACEHOLDER_ASPECT), pageWidth: width };
    }

    // Upright is the page as it comes out of the file - the reviewer's rotation
    // is deliberately excluded, which is the whole point: turning the page
    // cannot move the box.
    const upright = quarterTurn(intrinsicRotation)
      ? { w: pageSize.height, h: pageSize.width }
      : { w: pageSize.width, h: pageSize.height };
    const boxHeight = Math.round(width * (upright.h / upright.w));

    const shown = quarterTurn(displayRotation)
      ? { w: pageSize.height, h: pageSize.width }
      : { w: pageSize.width, h: pageSize.height };
    // Contain, not cover: a turned page letterboxes inside the box rather than
    // spilling out of it, because clipping the scan is worse than grey space.
    const fit = Math.min(width / shown.w, boxHeight / shown.h);

    return { boxHeight, pageWidth: Math.max(1, Math.round(shown.w * fit)) };
  }, [pageSize, width, intrinsicRotation, displayRotation]);

  // Each sheet is trimmed where its own content sits. A two-page invoice is two
  // passes through the scanner, and one rectangle across both would trim the
  // first correctly and clip the second.
  const crop = cropEnabled ? crops?.[current] || null : null;

  const setCropForPage = (page, next) =>
    onTransformChange?.((prev) => ({
      rotation: prev.rotation,
      crops: next
        ? { ...prev.crops, [page]: next }
        : Object.fromEntries(Object.entries(prev.crops).filter(([k]) => Number(k) !== page))
    }));

  const setCrop = (next) => setCropForPage(current, next);

  useEffect(() => {
    setCurrent(first);
    setError(null);
    setLoading(true);
    autoTrimmed.current = new Set();
  }, [url, first]);

  // Render at the container's width so the page is legible rather than a thumbnail.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const measure = () => setWidth(Math.max(240, el.clientWidth - 16));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // react-pdf re-fetches whenever this identity changes, so keep it stable.
  const file = useMemo(() => (url ? { url } : null), [url]);

  const go = (delta) => setCurrent((p) => Math.min(last, Math.max(first, p + delta)));

  // One write, not one per crop. Crops are expressed against the rendered page,
  // so every one turns with it - and the turn has to be part of the same update
  // as the rotation itself.
  const rotate = () =>
    onTransformChange?.((prev) => ({
      rotation: (prev.rotation + 90) % 360,
      crops: Object.fromEntries(
        Object.entries(prev.crops)
          .filter(([, rect]) => rect)
          .map(([page, rect]) => [page, rotateRectClockwise(rect)])
      )
    }));

  /** Detects the content on the page as rendered right now. */
  const autoTrim = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Nothing detectable means the page is already tight, or blank. Either way
    // leaving it whole is the safe answer.
    setCrop(detectContentBounds(canvas) || null);
  };

  const toggleCrop = () => {
    if (cropEnabled) {
      // Off clears every page, so nothing is filed cropped behind the
      // reviewer's back once the boxes are no longer on screen.
      setCropEnabled(false);
      onTransformChange?.((prev) => ({ rotation: prev.rotation, crops: {} }));
      return;
    }
    setCropEnabled(true);
    autoTrimmed.current = new Set();
    autoTrim();
  };

  /**
   * Trim once per page, and only if the reviewer has not already had a say.
   * Re-running after a Reset would just undo them.
   */
  const handleRendered = useCallback(() => {
    if (readOnly || !cropEnabled) return; // nothing here is being changed
    const key = `${current}:${displayRotation}`;
    if (autoTrimmed.current.has(key)) return;
    autoTrimmed.current.add(key);
    if (crop) return; // this page already has one, auto or hand-drawn

    const canvas = canvasRef.current;
    if (!canvas) return;
    const detected = detectContentBounds(canvas);
    if (detected) setCropForPage(current, detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, displayRotation, crop, cropEnabled, readOnly]);

  /* ------------------------------------------------------------ crop dragging */

  const pointFromEvent = (event) => {
    const box = pageBoxRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) return null;
    return {
      x: clamp01((event.clientX - box.left) / box.width),
      y: clamp01((event.clientY - box.top) / box.height)
    };
  };

  const startDrag = (mode) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromEvent(event);
    if (!point) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ mode, origin: point, startRect: crop });
  };

  const onPointerMove = (event) => {
    if (!drag) return;
    const point = pointFromEvent(event);
    if (!point) return;

    if (drag.mode === 'new') {
      setCrop({
        x: Math.min(drag.origin.x, point.x),
        y: Math.min(drag.origin.y, point.y),
        width: Math.abs(point.x - drag.origin.x),
        height: Math.abs(point.y - drag.origin.y)
      });
      return;
    }

    const r = drag.startRect;
    if (!r) return;

    if (drag.mode === 'move') {
      const dx = point.x - drag.origin.x;
      const dy = point.y - drag.origin.y;
      setCrop({
        x: clamp01(Math.min(r.x + dx, 1 - r.width)),
        y: clamp01(Math.min(r.y + dy, 1 - r.height)),
        width: r.width,
        height: r.height
      });
      return;
    }

    // Corner handles: the opposite corner stays put.
    const left = drag.mode.includes('w') ? point.x : r.x;
    const top = drag.mode.includes('n') ? point.y : r.y;
    const right = drag.mode.includes('e') ? point.x : r.x + r.width;
    const bottom = drag.mode.includes('s') ? point.y : r.y + r.height;

    setCrop({
      x: Math.min(left, right),
      y: Math.min(top, bottom),
      width: Math.abs(right - left),
      height: Math.abs(bottom - top)
    });
  };

  const endDrag = () => setDrag(null);

  if (!url) {
    return (
      <div className={cn('flex items-center justify-center rounded border border-dashed border-slate-300 p-6 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400', className)}>
        No file available to preview
      </div>
    );
  }

  const cropped = crop && crop.width > 0.02 && crop.height > 0.02;
  const pct = (n) => `${(n * 100).toFixed(3)}%`;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        ref={containerRef}
        className="relative flex min-h-[300px] items-start justify-center overflow-auto rounded border border-slate-200 bg-slate-100 p-2 dark:border-slate-800 dark:bg-slate-950"
      >
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading scan...
          </div>
        )}

        {error ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <AlertCircle className="h-6 w-6 text-amber-500" />
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Could not load the scan
            </p>
            <p className="max-w-[16rem] text-[11px] text-slate-500 dark:text-slate-400">{error}</p>
          </div>
        ) : (
          <Document
            file={file}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              setLoading(false);
            }}
            onLoadError={(e) => {
              setError(e?.message || 'The file could not be read.');
              setLoading(false);
            }}
            loading=""
            error=""
          >
            {/* The stable footprint. Fixed size, page centred inside it, so a
                rotation reflows nothing outside this box. */}
            <div
              className="flex items-center justify-center"
              style={{ width, height: viewport.boxHeight }}
            >
              {/* Sized by the page itself, because every crop rect is normalised
                  against the rendered page - measuring the drag or drawing the
                  overlay on the letterboxed box instead would offset both. */}
              <div
                ref={pageBoxRef}
                className="relative touch-none select-none"
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <Page
                  pageNumber={Math.min(current, numPages || current)}
                  width={viewport.pageWidth}
                  rotate={displayRotation}
                  canvasRef={canvasRef}
                  onLoadSuccess={(page) => {
                    setIntrinsicRotation(page.rotate || 0);
                    // Unrotated, so the box is the same whichever way the
                    // reviewer has turned the page when this fires.
                    const view = page.getViewport({ scale: 1, rotation: 0 });
                    setPageSize((prev) =>
                      prev?.width === view.width && prev?.height === view.height
                        ? prev
                        : { width: view.width, height: view.height }
                    );
                  }}
                  onRenderSuccess={handleRendered}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading=""
                />

                {/* Crop surface, only while cropping is on. Dragging on bare page
                    starts a fresh rectangle, so a bad auto-detection is one gesture
                    away from corrected. */}
                {cropEnabled && !readOnly && (
                  <div
                    className="absolute inset-0 cursor-crosshair"
                    onPointerDown={startDrag('new')}
                    role="presentation"
                  />
                )}

                {cropped && (
                  <>
                    {/* Dim what will be discarded rather than hide it - the
                        reviewer needs to see what they are cutting off. */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 bg-slate-900/50" style={{ height: pct(crop.y) }} />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-slate-900/50" style={{ height: pct(1 - crop.y - crop.height) }} />
                    <div className="pointer-events-none absolute left-0 bg-slate-900/50" style={{ top: pct(crop.y), height: pct(crop.height), width: pct(crop.x) }} />
                    <div className="pointer-events-none absolute right-0 bg-slate-900/50" style={{ top: pct(crop.y), height: pct(crop.height), width: pct(1 - crop.x - crop.width) }} />

                    <div
                      className="absolute cursor-move border-2 border-blue-500 shadow-[0_0_0_1px_rgba(255,255,255,0.6)]"
                      style={{ left: pct(crop.x), top: pct(crop.y), width: pct(crop.width), height: pct(crop.height) }}
                      onPointerDown={startDrag('move')}
                      role="presentation"
                    >
                      {['nw', 'ne', 'sw', 'se'].map((corner) => (
                        <span
                          key={corner}
                          onPointerDown={startDrag(corner)}
                          className={cn(
                            'absolute h-3 w-3 rounded-sm border border-white bg-blue-500',
                            corner === 'nw' && '-left-1.5 -top-1.5 cursor-nwse-resize',
                            corner === 'ne' && '-right-1.5 -top-1.5 cursor-nesw-resize',
                            corner === 'sw' && '-bottom-1.5 -left-1.5 cursor-nesw-resize',
                            corner === 'se' && '-bottom-1.5 -right-1.5 cursor-nwse-resize'
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Document>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => go(-1)}
            disabled={current <= first}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="min-w-[7rem] text-center text-[11px] tabular-nums text-slate-600 dark:text-slate-400">
            {pageCount > 1 ? `page ${current} of ${first}–${last}` : `page ${first}`}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => go(1)}
            disabled={current >= last}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          {!readOnly && (
            <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={rotate}
            aria-label="Rotate 90 degrees clockwise"
            title="Rotate. The archived copy keeps this orientation."
          >
            <RotateCw className="h-3.5 w-3.5" />
            {rotation !== 0 && <span className="ml-1 tabular-nums">{rotation}°</span>}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={cropEnabled}
            className={cn(
              'h-7 px-2 text-[11px]',
              cropEnabled && 'border-blue-400 text-blue-700 dark:border-blue-600 dark:text-blue-300'
            )}
            onClick={toggleCrop}
            title={
              cropEnabled
                ? 'Turn cropping off and archive whole pages. Rotate freely, then turn it back on to trim.'
                : 'Trim to the content on the page, as it is turned now.'
            }
          >
            <Crop className="mr-1 h-3.5 w-3.5" />
            {cropEnabled ? 'Cropping' : 'Crop off'}
          </Button>

          {cropEnabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              onClick={autoTrim}
              aria-label={`Re-detect the crop on page ${current}`}
              title="Re-detect the crop on this page"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          </>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            onClick={onExpand}
            aria-label={`Open ${fileName || 'scan'} full size`}
          >
            <Maximize2 className="mr-1 h-3 w-3" />
            Full size
          </Button>
        </div>
      </div>

      {!readOnly && (
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {!cropEnabled
          ? 'Cropping is off - the whole sheet will be filed. Turn the page the right way up first, then switch cropping on to trim it.'
          : cropped
            ? pageCount > 1
              ? `Cropping page ${current}. Each page is trimmed separately - check the others too.`
              : 'Filing the cropped page. Drag the box to adjust, or turn cropping off to keep the whole sheet.'
            : 'Nothing to trim on this page - drag a box to crop it by hand.'}
      </p>
      )}
    </div>
  );
}


export default ReviewPreview;
